// TMDB: constantes + transformações de URL de imagem, compartilhadas entre
// backend e cliente.
//
// POR QUE AS TRANSFORMAÇÕES MORAM AQUI (e não só no backend)
// ----------------------------------------------------------
// O TMDB serve as variantes de tamanho NA PRÓPRIA URL
// (`https://image.tmdb.org/t/p/<size>/<path>`) — não é preciso intermediário
// pra pedir w342 em vez de w500. Até a v2.11.2 quem fazia essa reescrita era só
// o nosso image-proxy (`/v1/proxy/img?w=`), porque no NAVEGADOR o proxy é
// necessário de qualquer jeito (mixed content: página https não carrega imagem
// http, e painéis Xtream servem http). Na TV EMPACOTADA (file://) essa
// restrição não existe e o proxy vira só custo: milhares de TVs × centenas de
// capas cada, de banda nossa, pra servir uma imagem que a origem já serve.
//
// Então a TV monta a URL do TMDB DIRETO — com a MESMA lógica de tamanho que o
// backend aplicaria. Pra cliente e backend não divergirem (e a capa não sair
// borrada num e nítida no outro), as duas funções puras vivem aqui e são
// importadas pelos dois lados:
//   - apps/api/src/routes/image-proxy.ts  (caminho WEB, via proxy)
//   - apps/tv/src/lib/image-source.ts     (caminho TV, direto)
//
// Lista oficial de tamanhos: https://developer.themoviedb.org/docs/image-basics
export const TMDB_VALID_SIZES = [
    'w92',
    'w154',
    'w185',
    'w300',
    'w342',
    'w500',
    'w780',
    'w1280',
    'original',
];
/** Type guard pra validar string em runtime. */
export function isTmdbValidSize(s) {
    return TMDB_VALID_SIZES.includes(s);
}
/**
 * Normaliza host TMDB pro CDN canônico. Provedores Xtream às vezes sobem
 * capas com a URL do SITE (`www.themoviedb.org/t/p/...` ou
 * `themoviedb.org/t/p/...`), que responde **301 redirect** pro CDN
 * (`image.tmdb.org/t/p/...`). Sem essa normalização, a imagem cai no
 * caminho não-trusted/redirect do proxy (mais lento e frágil) e, quando
 * esse caminho falha, vira placeholder de letras mesmo a capa existindo.
 * Direto na TV, um 301 em `<img>` até segue — mas custa um round-trip por
 * capa num device fraco; e nem todo firmware antigo segue redirect de imagem
 * com a mesma robustez.
 *
 * Reescrevendo pro CDN, a URL vira trusted (HTTPS direto, sem redirect) e
 * serve 200 na hora. Idempotente em URLs que já são `image.tmdb.org` ou
 * não-TMDB (logos Xtream etc ficam intactos).
 *
 * `URL` é suportado desde Chrome 32 — dentro do piso de compat (Chr38).
 */
export function normalizeTmdbHost(rawUrl) {
    // Parse via URL pra usar o hostname JÁ canonicalizado — assim formas com
    // userinfo (`user@www.themoviedb.org`), porta explícita (`:443`) ou
    // maiúsculas resolvem pro host real e não escapam da normalização (review
    // ALTO: regex na string crua deixava esses casos voltarem a virar
    // placeholder). URL malformada passa intacta (validateUrl barra depois, no
    // backend; no cliente o <img> simplesmente falha e cai no placeholder).
    let u;
    try {
        u = new URL(rawUrl);
    }
    catch {
        return rawUrl;
    }
    const host = u.hostname.toLowerCase();
    const isTmdbSite = host === 'themoviedb.org' || host === 'www.themoviedb.org';
    // Só toca em hosts do SITE do TMDB servindo path de imagem (/t/p/). CDN
    // (image.tmdb.org) já é o destino; capas do provider passam intactas.
    if (!isTmdbSite || !u.pathname.startsWith('/t/p/'))
        return rawUrl;
    return `https://image.tmdb.org${u.pathname}${u.search}`;
}
/**
 * Reescreve URL TMDB pra usar size desejado. Só age em URLs do tipo
 * `https://image.tmdb.org/t/p/<size>/<path>` — outras URLs (logos
 * Xtream, capas de painel, etc) ficam **intactas**. É essa idempotência que
 * permite ao caller chamar a função em CIMA de qualquer src sem perguntar a
 * origem antes.
 *
 * Idempotente também no size: passar `w500` numa URL que já é `w500` retorna a
 * mesma URL (cache hit no upstream).
 *
 * `requestedSize` null/inválido = passthrough (não inventa tamanho).
 */
export function rewriteTmdbSize(rawUrl, requestedSize) {
    if (!requestedSize)
        return rawUrl;
    if (!isTmdbValidSize(requestedSize))
        return rawUrl;
    const tmdbMatch = rawUrl.match(/^(https?:\/\/image\.tmdb\.org\/t\/p\/)([^/]+)(\/.*)$/i);
    if (!tmdbMatch)
        return rawUrl;
    return `${tmdbMatch[1]}${requestedSize}${tmdbMatch[3]}`;
}
//# sourceMappingURL=tmdb.js.map